import moment from 'moment-timezone';
import { PROCESSED_TIMESHEET_STATUS_ENUM } from '../constants';

const EASTERN_TZ = 'America/New_York';

// Grace-period duration (in business days) is supplied via an environment variable so it can be
// tuned per environment without a redeploy (the app just needs a restart). If the value is
// missing or invalid we fall back to the agreed default of the first 3 business days of the month.
const DEFAULT_GRACE_PERIOD_DAYS = 3;

export const MONTH_POSTED_WINDOW_WARNING_MESSAGES = {
    GRACE_PERIOD_EXPIRED:
        'The previous month grace period has ended. Month Posted must be within the current posting window before this Processed Timesheet can be sent to Workday.',
};

export const getMonthPostedGracePeriodDays = () => {
    const parsed = parseInt(process.env.REACT_APP_PTS_MONTH_POSTED_GRACE_PERIOD_DAYS, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_GRACE_PERIOD_DAYS;
};

// The Month Posted window warning only applies when sending a PTS to Workday, i.e. when
// changing to Resolved, or to Invoice Review while the timesheet is a correction.
export const requiresMonthPostedWindowValidation = (newStatus, isCorrection = false) =>
    newStatus === PROCESSED_TIMESHEET_STATUS_ENUM.Resolved ||
    (newStatus === PROCESSED_TIMESHEET_STATUS_ENUM.InvoiceReview && Boolean(isCorrection));

// 6:00 PM ET on the last day of the month that `m` falls in.
const sixPmOnLastDayOfMonth = (m) => m.clone().endOf('month').startOf('day').add(18, 'hours');

// Normalizes the /holidays payload (array of { date: 'YYYY-MM-DD' } or plain date strings)
// into a set of 'YYYY-MM-DD' strings for quick lookup.
const toHolidaySet = (holidays = []) =>
    new Set(
        (Array.isArray(holidays) ? holidays : [])
            .map((holiday) => (typeof holiday === 'string' ? holiday : holiday?.date))
            .filter(Boolean)
    );

const isBusinessDay = (m, holidaySet) => {
    const dayOfWeek = m.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
    }
    return !holidaySet.has(m.format('YYYY-MM-DD'));
};

// Midnight ET at the end of the Nth business day of `monthStart`'s month, i.e. the moment the
// grace period expires. Business days exclude weekends and the supplied (LT) holidays.
const getGracePeriodEnd = (monthStart, gracePeriodDays, holidaySet) => {
    if (gracePeriodDays <= 0) {
        return monthStart.clone();
    }

    const cursor = monthStart.clone().startOf('day');
    let businessDaysCounted = 0;
    let lastBusinessDay = null;

    // Cap the search so a malformed holiday list can never spin forever.
    for (let i = 0; i < 62 && businessDaysCounted < gracePeriodDays; i += 1) {
        if (isBusinessDay(cursor, holidaySet)) {
            businessDaysCounted += 1;
            lastBusinessDay = cursor.clone();
        }
        if (businessDaysCounted < gracePeriodDays) {
            cursor.add(1, 'day');
        }
    }

    // Grace runs through the end of the Nth business day (i.e. midnight starting the next day).
    return (lastBusinessDay ?? monthStart.clone()).add(1, 'day').startOf('day');
};

/**
 * Determines whether a Month Posted warning should be shown for a status change.
 *
 * The Month Posted date is considered "inside" the current posting window when it is AFTER 6pm ET
 * on the last day of the previous month AND BEFORE 6pm ET on the last day of the current month.
 * When it falls outside that window, no warning is shown if the status change still qualifies for
 * the previous-month grace period (the first N business days of the posting month, expiring at
 * midnight ET). The warning is only shown once that grace period has ended. The status change
 * itself is always allowed regardless of the result.
 *
 * @param {Array} [params.holidays] LT holidays (from the /holidays endpoint) excluded, along with
 *   weekends, when counting the business days of the grace period.
 * @returns {string|null} the grace-period-expired warning, or null when no warning applies.
 */
export const getMonthPostedWindowWarning = ({
    monthPostedDate,
    newStatus,
    isCorrection = false,
    now,
    graceDays,
    holidays = [],
} = {}) => {
    if (!requiresMonthPostedWindowValidation(newStatus, isCorrection) || !monthPostedDate) {
        return null;
    }

    const gracePeriodDays = Number.isFinite(graceDays)
        ? graceDays
        : getMonthPostedGracePeriodDays();
    const nowEt = now ? moment.tz(now, EASTERN_TZ) : moment.tz(EASTERN_TZ);
    if (!nowEt.isValid()) {
        return null;
    }

    // Month Posted is stored as a UTC-midnight month marker; evaluate its calendar date in ET.
    const monthPostedEt = moment.tz(
        moment.utc(monthPostedDate).format('YYYY-MM-DD'),
        'YYYY-MM-DD',
        EASTERN_TZ
    );
    if (!monthPostedEt.isValid()) {
        return null;
    }

    // The posting month containing "now" rolls forward after 6pm ET on the last day of the month.
    let postingMonthStart = nowEt.clone().startOf('month');
    if (nowEt.isSameOrAfter(sixPmOnLastDayOfMonth(nowEt))) {
        postingMonthStart = nowEt.clone().add(1, 'month').startOf('month');
    }

    const windowStart = sixPmOnLastDayOfMonth(postingMonthStart.clone().subtract(1, 'month'));
    const windowEnd = sixPmOnLastDayOfMonth(postingMonthStart);

    const isInsideWindow = monthPostedEt.isAfter(windowStart) && monthPostedEt.isBefore(windowEnd);
    if (isInsideWindow) {
        return null;
    }

    // Outside the window: previous-month postings are still allowed (with no warning) during the
    // first `gracePeriodDays` business days of the posting month. Warn only after that expires.
    const graceEnd = getGracePeriodEnd(postingMonthStart, gracePeriodDays, toHolidaySet(holidays));
    if (nowEt.isBefore(graceEnd)) {
        return null;
    }

    return MONTH_POSTED_WINDOW_WARNING_MESSAGES.GRACE_PERIOD_EXPIRED;
};
